import { useCallback, useRef, useState } from 'react';
import { Loader2, Upload, CheckCircle2, XCircle, FileUp } from 'lucide-react';

const ACCEPT = 'application/pdf,image/*';

/**
 * Wide, shallow drop-zone for the Cost Inbox — drag & drop or click, many files
 * at once. Uploads in one multipart request; shows per-file outcome.
 */
export function InboxDropZone({ api, onUploaded }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]); // [{name, ok, error}]

  const upload = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setBusy(true);
    setResults(files.map(f => ({ name: f.name, pending: true })));
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await api.post('/cost-inbox/upload', fd);
      const count = r.data?.count ?? (r.data?.items?.length || 1);
      setResults(files.map(f => ({ name: f.name, ok: true })));
      onUploaded?.(count);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      setResults(files.map(f => ({ name: f.name, ok: false, error: msg })));
    } finally {
      setBusy(false);
      setTimeout(() => setResults([]), 6000);
    }
  }, [api, onUploaded]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    if (busy) return;
    upload(e.dataTransfer?.files);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !busy && inputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
      onDrop={onDrop}
      className={`relative w-full rounded-xl border-2 border-dashed px-5 py-4 mb-5 transition-colors cursor-pointer select-none
        ${dragging ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/15 bg-white/[0.03] hover:border-white/30 hover:bg-white/[0.05]'}`}
      data-testid="inbox-dropzone"
    >
      <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onClick={(e) => e.stopPropagation()}
        onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid="inbox-file-input" />
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${dragging ? 'bg-[#F5A623]/25' : 'bg-white/5'}`}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin text-[#F5A623]" /> : <FileUp className={`w-5 h-5 ${dragging ? 'text-[#F5A623]' : 'text-gray-300'}`} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {dragging ? 'Drop to add to the inbox' : busy ? `Uploading ${results.length} file${results.length === 1 ? '' : 's'}…` : 'Drop invoices here, or click to choose files'}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">PDF or photos · up to 20 at once · 25MB each · the AI reads each one and pre-fills supplier, total and line items</div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E2530] bg-[#F5A623] rounded-md px-3 py-1.5">
          <Upload className="w-3.5 h-3.5" /> Upload
        </span>
      </div>
      {results.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="inbox-upload-results">
          {results.map((r, i) => (
            <span key={`${r.name}-${i}`} title={r.error || ''}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border
                ${r.pending ? 'border-white/10 text-gray-400' : r.ok ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10' : 'border-red-500/30 text-red-300 bg-red-500/10'}`}>
              {r.pending ? <Loader2 className="w-3 h-3 animate-spin" /> : r.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              <span className="truncate max-w-[220px]">{r.name}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
